import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GraduationCap, Menu, X, User, LogOut, BookOpen, Calendar, Sparkles, Video, Settings, ShoppingBag } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, isAuthenticated, isSubscribed, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="header-glass" data-testid="header">
      <nav className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <div className="w-10 h-10 bg-[#002FA7] rounded-sm flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-black tracking-tight text-[#0A0A0A]">RIKIRTUTOR</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <Link 
              to="/courses" 
              className="text-sm font-medium text-[#4B5563] hover:text-[#002FA7] transition-colors"
              data-testid="nav-courses"
            >
              Programs
            </Link>
            <Link 
              to="/courses?category=GED" 
              className="text-sm font-medium text-[#4B5563] hover:text-[#002FA7] transition-colors"
              data-testid="nav-ged"
            >
              GED Prep
            </Link>
            <Link 
              to="/courses?category=SAT" 
              className="text-sm font-medium text-[#4B5563] hover:text-[#002FA7] transition-colors"
              data-testid="nav-sat"
            >
              SAT Prep
            </Link>
            <Link 
              to="/pricing" 
              className="text-sm font-medium text-[#002FA7] hover:text-[#001B63] transition-colors"
              data-testid="nav-subscribe"
            >
              Pricing
            </Link>
            <Link 
              to="/schedule" 
              className="text-sm font-medium text-[#4B5563] hover:text-[#002FA7] transition-colors"
              data-testid="nav-schedule"
            >
              Schedule
            </Link>
            <Link 
              to="/study-rooms" 
              className="text-sm font-medium text-[#4B5563] hover:text-[#002FA7] transition-colors"
              data-testid="nav-study-rooms"
            >
              Study Rooms
            </Link>
            <Link 
              to="/contact" 
              className="text-sm font-medium text-[#4B5563] hover:text-[#002FA7] transition-colors"
              data-testid="nav-contact"
            >
              Contact
            </Link>
          </div>

          {/* Auth Section */}
          <div className="hidden md:flex items-center gap-4">
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="flex items-center gap-2 rounded-sm border-[#0A0A0A]/10"
                    data-testid="user-menu-trigger"
                  >
                    <User className="w-4 h-4" />
                    <span className="font-medium">{user?.name}</span>
                    {isSubscribed && (
                      <span className="text-xs bg-[#002FA7] text-white px-1.5 py-0.5 rounded-sm">PRO</span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 rounded-sm">
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="flex items-center gap-2" data-testid="menu-dashboard">
                      <BookOpen className="w-4 h-4" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  {isSubscribed && (
                    <>
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard?tab=book" className="flex items-center gap-2" data-testid="menu-book">
                          <Calendar className="w-4 h-4" />
                          Book Session
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard?tab=videos" className="flex items-center gap-2" data-testid="menu-videos">
                          <Video className="w-4 h-4" />
                          Video Library
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/dashboard?tab=ai" className="flex items-center gap-2" data-testid="menu-ai">
                          <Sparkles className="w-4 h-4" />
                          AI Assistant
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  {!isSubscribed && (
                    <DropdownMenuItem asChild>
                      <Link to="/pricing" className="flex items-center gap-2 text-[#002FA7]" data-testid="menu-subscribe">
                        <Sparkles className="w-4 h-4" />
                        View Plans
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center gap-2" data-testid="menu-profile">
                      <Settings className="w-4 h-4" />
                      My Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/purchases" className="flex items-center gap-2" data-testid="menu-purchases">
                      <ShoppingBag className="w-4 h-4" />
                      Purchase History
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleLogout} 
                    className="flex items-center gap-2 text-[#E53935]"
                    data-testid="menu-logout"
                  >
                    <LogOut className="w-4 h-4" />
                    Log Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="font-medium" data-testid="login-btn">
                    Log In
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button className="bg-[#002FA7] hover:bg-[#001B63] text-white font-bold rounded-sm" data-testid="signup-btn">
                    Book Now
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-[#E5E7EB] pt-4 animate-fade-in">
            <div className="flex flex-col gap-4">
              <Link 
                to="/courses" 
                className="text-sm font-medium text-[#4B5563] hover:text-[#002FA7]"
                onClick={() => setMobileMenuOpen(false)}
              >
                Programs
              </Link>
              <Link 
                to="/courses?category=GED" 
                className="text-sm font-medium text-[#4B5563] hover:text-[#002FA7]"
                onClick={() => setMobileMenuOpen(false)}
              >
                GED Prep
              </Link>
              <Link 
                to="/courses?category=SAT" 
                className="text-sm font-medium text-[#4B5563] hover:text-[#002FA7]"
                onClick={() => setMobileMenuOpen(false)}
              >
                SAT Prep
              </Link>
              <Link 
                to="/pricing" 
                className="text-sm font-bold text-[#002FA7]"
                onClick={() => setMobileMenuOpen(false)}
              >
                Pricing
              </Link>
              <Link 
                to="/schedule" 
                className="text-sm font-medium text-[#4B5563] hover:text-[#002FA7]"
                onClick={() => setMobileMenuOpen(false)}
              >
                Schedule
              </Link>
              <Link 
                to="/study-rooms" 
                className="text-sm font-medium text-[#4B5563] hover:text-[#002FA7]"
                onClick={() => setMobileMenuOpen(false)}
              >
                Study Rooms
              </Link>
              <Link 
                to="/contact" 
                className="text-sm font-medium text-[#4B5563] hover:text-[#002FA7]"
                onClick={() => setMobileMenuOpen(false)}
              >
                Contact
              </Link>
              <div className="border-t border-[#E5E7EB] pt-4 mt-2">
                {isAuthenticated ? (
                  <>
                    <Link 
                      to="/dashboard" 
                      className="block text-sm font-medium text-[#4B5563] hover:text-[#002FA7] mb-3"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Dashboard
                    </Link>
                    <Link 
                      to="/profile" 
                      className="block text-sm font-medium text-[#4B5563] hover:text-[#002FA7] mb-3"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="mobile-profile-link"
                    >
                      My Profile
                    </Link>
                    <Link 
                      to="/purchases" 
                      className="block text-sm font-medium text-[#4B5563] hover:text-[#002FA7] mb-3"
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid="mobile-purchases-link"
                    >
                      Purchase History
                    </Link>
                    <button
                      onClick={() => {
                        handleLogout();
                        setMobileMenuOpen(false);
                      }}
                      className="text-sm font-medium text-[#E53935]"
                    >
                      Log Out
                    </button>
                  </>
                ) : (
                  <div className="flex gap-3">
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="rounded-sm">Log In</Button>
                    </Link>
                    <Link to="/pricing" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="bg-[#002FA7] hover:bg-[#001B63] text-white rounded-sm">Book Now</Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
